import React, { useState } from 'react';
import Spinner from '@/components/common/Spinner';
import Icon from '@/components/ui/Icon';
import cn from '@/utils/cn';
import { useStandardsContent } from './standardsContentContext';
import { CorrelatedContentItem, CorrelatedContentUnit } from './types';
import useCorrelatedContent from './useCorrelatedContent';

const CAP = 20;

// Trim the flattened item count across all groups (unit items → lesson items → reference
// materials) to `limit`, dropping now-empty lessons/units.
const capGroups = (units: CorrelatedContentUnit[], referenceMaterials: CorrelatedContentItem[], limit: number) => {
  let budget = limit;
  const cappedUnits = units.map((unit) => {
    const items = unit.items.slice(0, Math.max(0, budget));
    budget -= items.length;
    const lessons = unit.lessons.map((lesson) => {
      const lessonItems = lesson.items.slice(0, Math.max(0, budget));
      budget -= lessonItems.length;
      return { ...lesson, items: lessonItems };
    }).filter(lesson => lesson.items.length > 0);
    return { ...unit, items, lessons };
  }).filter(unit => unit.items.length > 0 || unit.lessons.length > 0);

  return { referenceMaterials: referenceMaterials.slice(0, Math.max(0, budget)), units: cappedUnits };
};

// Each item links to its sysadmin content editor, opened in a new tab (M1.3 / PLAT-17665). A real
// anchor (not an onClick) gives right-click / cmd-click / new-tab behavior for free and leaves the
// standards tree untouched behind the new tab. url can be null if the server couldn't resolve an
// editor (a reported, effectively-unreachable case) — fall back to plain text rather than a dead link.
const ContentItemLink = ({ item }: { item: CorrelatedContentItem }) => {
  if (!item.url) {
    return <>{item.label}</>;
  }

  return (
    <a
      className="tw-inline-flex tw-items-center tw-gap-1 tw-text-tci-purple tw-no-underline hover:tw-underline"
      href={item.url}
      rel="noopener noreferrer"
      target="_blank"
    >
      {item.label}
      <Icon aria-hidden icon="ExternalLink" size={12} />
    </a>
  );
};

interface CorrelatedContentPanelProps {
  gradeLabel?: string;
  standardId: number;
}

// Inline block shown under an expanded TAGGABLE leaf: the program's content correlated to that
// standard, grouped Unit → Lesson → item, with a program-level Reference Materials bucket.
// Item labels come from the API (M1.2); they become links in M1.3 (PLAT-17665). Capped at CAP
// with a "Show all N" reveal. See DESIGN-340.
const CorrelatedContentPanel = ({ gradeLabel, standardId }: CorrelatedContentPanelProps) => {
  const { programId, programName, stateLabel, toggleContentLeaf } = useStandardsContent();
  const { data, isLoading } = useCorrelatedContent({ enabled: true, programId, standardId });
  const [showAll, setShowAll] = useState(false);

  const scope = ['Correlated Content', programName, stateLabel, gradeLabel].filter(Boolean).join(' · ');

  const allUnits = data?.units ?? [];
  const allReferenceMaterials = data?.reference_materials ?? [];
  const { units, referenceMaterials } = showAll
    ? { referenceMaterials: allReferenceMaterials, units: allUnits }
    : capGroups(allUnits, allReferenceMaterials, CAP);

  return (
    <div className={cn(
      'tw-my-2 tw-ml-8 tw-mr-3 tw-rounded-lg tw-p-4',
      'tw-border tw-border-solid tw-border-light-grey tw-bg-gray-50',
    )}
    >
      <div className="tw-flex tw-items-start tw-justify-between tw-gap-2">
        <span className="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-dark-grey">
          {scope}
        </span>
        <button
          aria-label="Close correlated content"
          className="tw-flex-shrink-0 tw-bg-transparent tw-border-0 tw-cursor-pointer tw-text-dark-grey tw-p-0"
          type="button"
          onClick={() => toggleContentLeaf(standardId)}
        >
          <Icon icon="Close" size={12} />
        </button>
      </div>

      {isLoading && (
        <div className="tw-flex tw-items-center tw-gap-2 tw-py-3 tw-text-sm tw-text-dark-grey">
          <Spinner /> Loading&hellip;
        </div>
      )}

      {!isLoading && (data?.total ?? 0) === 0 && (
        <p className="tw-text-sm tw-text-dark-grey tw-mt-2 tw-mb-0">No correlated content in this program.</p>
      )}

      {!isLoading && units.map(unit => (
        <div key={unit.title} className="tw-mt-3">
          <p className="tw-text-sm tw-font-bold tw-mb-1">{unit.title}</p>
          {unit.items.length > 0 && (
            <ul className="tw-list-none tw-m-0 tw-p-0 tw-pl-3 tw-mb-2">
              {unit.items.map(item => (
                <li key={`${item.model_type}-${item.model_id}`} className="tw-text-sm tw-py-0.5">
                  <ContentItemLink item={item} />
                </li>
              ))}
            </ul>
          )}
          {unit.lessons.map(lesson => (
            <div key={lesson.title} className="tw-mb-2 tw-pl-3 tw-border-l-2 tw-border-solid tw-border-light-grey">
              <p className="tw-text-sm tw-text-dark-grey tw-mb-1">{lesson.title}</p>
              <ul className="tw-list-none tw-m-0 tw-p-0">
                {lesson.items.map(item => (
                  <li key={`${item.model_type}-${item.model_id}`} className="tw-text-sm tw-py-0.5">
                    <ContentItemLink item={item} />
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      ))}

      {!isLoading && referenceMaterials.length > 0 && (
        <div className="tw-mt-3">
          <p className="tw-text-sm tw-font-bold tw-mb-1">Reference Materials</p>
          <ul className="tw-list-none tw-m-0 tw-p-0 tw-pl-3">
            {referenceMaterials.map(item => (
              <li key={`${item.model_type}-${item.model_id}`} className="tw-text-sm tw-py-0.5">
                <ContentItemLink item={item} />
              </li>
            ))}
          </ul>
        </div>
      )}

      {!isLoading && !showAll && (data?.total ?? 0) > CAP && (
        <button
          className="btn btn--outline-purple tw-px-3 tw-text-sm tw-mt-3"
          type="button"
          onClick={() => setShowAll(true)}
        >
          {`Show all ${data?.total} correlations`}
        </button>
      )}
    </div>
  );
};

export default CorrelatedContentPanel;
